import type { Movie, DiscoverResponse, Filters, MediaType, MediaItem, TVShow } from '../types/movie';
import { TV_GENRE_KEYWORD_MAP, UNIFIED_GENRES, TV_ONLY_GENRES } from '../utils/constants';
import { apiFetch } from '../lib/api';

// Build genre and keyword params for TV discover
function buildTVGenreParams(selectedGenres: number[]): { genres: string | undefined; keywords: string | undefined } {
  const genreIds: number[] = [];
  const keywordIds: number[] = [];

  selectedGenres.forEach(genreId => {
    // Check if this genre needs keyword workaround
    if (TV_GENRE_KEYWORD_MAP[genreId]) {
      keywordIds.push(...TV_GENRE_KEYWORD_MAP[genreId]);
    } else {
      // Find the TV equivalent genre ID
      const unifiedGenre = [...UNIFIED_GENRES, ...TV_ONLY_GENRES].find(g => g.id === genreId);
      if (unifiedGenre && typeof unifiedGenre.tvId === 'number') {
        genreIds.push(unifiedGenre.tvId);
      } else {
        // Fallback: use the genre ID directly if it's a native TV genre
        genreIds.push(genreId);
      }
    }
  });

  return {
    genres: genreIds.length > 0 ? genreIds.join('|') : undefined,
    keywords: keywordIds.length > 0 ? keywordIds.join('|') : undefined,
  };
}

interface FetchOptions {
  endpoint: string;
  params?: Record<string, string | number | boolean | undefined>;
}

/**
 * Internal fetch wrapper using apiFetch
 * Builds query params and delegates to centralized apiFetch
 */
async function fetchFromAPI<T>({ endpoint, params = {} }: FetchOptions): Promise<T> {
  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== '') {
      searchParams.append(key, String(value));
    }
  });

  const queryString = searchParams.toString();
  const path = queryString ? `${endpoint}?${queryString}` : endpoint;

  // Use centralized apiFetch (no auth needed for public TMDB/OMDB endpoints)
  return apiFetch<T>(path);
}

// Helper to convert TV response to unified MediaItem format
function normalizeToMediaItem(item: Movie | TVShow, mediaType: MediaType): MediaItem {
  if (mediaType === 'tv') {
    const tv = item as TVShow;
    return {
      id: tv.id,
      title: tv.name,
      overview: tv.overview,
      poster_path: tv.poster_path,
      backdrop_path: tv.backdrop_path,
      release_date: tv.first_air_date || '',
      genre_ids: tv.genre_ids || [],
      vote_average: tv.vote_average,
      vote_count: tv.vote_count,
      popularity: tv.popularity,
      original_language: tv.original_language,
      runtime: tv.runtime ?? tv.episode_run_time?.[0],
      media_type: 'tv',
      imdb_rating: tv.imdb_rating,
      rt_rating: tv.rt_rating,
      filmpl_rating: tv.filmpl_rating,
      number_of_seasons: tv.number_of_seasons,
    };
  }
  const movie = item as Movie;
  return {
    ...movie,
    media_type: 'movie',
  };
}

export interface MediaDiscoverResponse {
  page: number;
  results: MediaItem[];
  total_pages: number;
  total_results: number;
}

export async function discoverMedia(
  filters: Partial<Filters>,
  page = 1,
  mediaType: MediaType = 'movie'
): Promise<MediaDiscoverResponse> {
  const params: Record<string, string | number | undefined> = {
    page,
    sort_by: filters.sortBy,
    // Popular gate v1: (vote_count >= 50 OR popularity >= 4) - applies to both movies and TV
    popular_gate: filters.popularOnly !== false ? 'v1' : undefined,
    // Released only: exclude unreleased/future-dated items
    released_only: filters.releasedOnly !== false ? 'true' : undefined,
    // When sorting by IMDb votes, gate to items that have votes (§2.2)
    'imdb_votes.gte': filters.sortBy?.startsWith('imdb_votes.') ? 1 : undefined,
  };

  // Genre filter - handle TV genre/keyword mapping
  if (filters.genres && filters.genres.length > 0) {
    if (mediaType === 'tv') {
      // Use keyword workaround for genres that don't exist natively in TV
      const { genres, keywords } = buildTVGenreParams(filters.genres);
      if (genres) {
        params.with_genres = genres;
      }
      if (keywords) {
        params.with_keywords = keywords;
      }
    } else {
      params.with_genres = filters.genres.join(',');
    }
  }

  // Year range - different params for TV vs Movie
  if (filters.yearRange) {
    if (mediaType === 'movie') {
      params['primary_release_date.gte'] = `${filters.yearRange[0]}-01-01`;
      params['primary_release_date.lte'] = `${filters.yearRange[1]}-12-31`;
    } else {
      params['first_air_date.gte'] = `${filters.yearRange[0]}-01-01`;
      params['first_air_date.lte'] = `${filters.yearRange[1]}-12-31`;
    }
  }

  // Runtime range (for movies) / Episode runtime for TV
  if (filters.runtimeRange) {
    if (filters.runtimeRange[0] > 0) {
      params['with_runtime.gte'] = filters.runtimeRange[0];
    }
    if (filters.runtimeRange[1] < 300) {
      params['with_runtime.lte'] = filters.runtimeRange[1];
    }
  }

  // TMDB rating range
  if (filters.tmdbRatingRange) {
    if (filters.tmdbRatingRange[0] > 0) {
      params['vote_average.gte'] = filters.tmdbRatingRange[0];
    }
    if (filters.tmdbRatingRange[1] < 10) {
      params['vote_average.lte'] = filters.tmdbRatingRange[1];
    }
  }

  // IMDB rating range (D1 server-side filtering)
  if (filters.ratingRange) {
    if (filters.ratingRange[0] > 0) {
      params['imdb_rating.gte'] = filters.ratingRange[0];
    }
    if (filters.ratingRange[1] < 10) {
      params['imdb_rating.lte'] = filters.ratingRange[1];
    }
  }

  // Rotten Tomatoes rating range (D1 server-side filtering)
  if (filters.rtRatingRange) {
    if (filters.rtRatingRange[0] > 0) {
      params['rt_rating.gte'] = filters.rtRatingRange[0];
    }
    if (filters.rtRatingRange[1] < 100) {
      params['rt_rating.lte'] = filters.rtRatingRange[1];
    }
  }

  // Filmpl rating range (Meilisearch server-side filtering)
  if (filters.ourRatingRange) {
    if (filters.ourRatingRange[0] > 0) {
      params['filmpl_rating.gte'] = filters.ourRatingRange[0];
    }
    if (filters.ourRatingRange[1] < 10) {
      params['filmpl_rating.lte'] = filters.ourRatingRange[1];
    }
  }

  // Language filter
  if (filters.languages && filters.languages.length > 0) {
    params.with_original_language = filters.languages.join('|');
  }

  // Person search (actor or director via cast/crew) - supports multiple selections
  if (filters.actorSearch && filters.actorSearch.length > 0) {
    const actorIds: number[] = [];
    for (const actorName of filters.actorSearch) {
      const people = await searchPeople(actorName);
      if (people.length > 0) {
        actorIds.push(people[0].id);
      }
    }
    if (actorIds.length > 0) {
      // Use comma for AND logic (must have all actors)
      params.with_cast = actorIds.join(',');
    }
  }

  if (filters.directorSearch && filters.directorSearch.length > 0) {
    const directorIds: number[] = [];
    for (const directorName of filters.directorSearch) {
      const people = await searchPeople(directorName);
      if (people.length > 0) {
        directorIds.push(people[0].id);
      }
    }
    if (directorIds.length > 0) {
      // Use comma for AND logic (must have all directors)
      params.with_crew = directorIds.join(',');
    }
  }

  const endpoint = mediaType === 'movie' ? '/discover/movie' : '/discover/tv';
  const response = await fetchFromAPI<DiscoverResponse>({
    endpoint,
    params,
  });

  return {
    ...response,
    results: response.results.map(item => normalizeToMediaItem(item, mediaType)),
  };
}

// Keep original function for backwards compatibility
export async function discoverMovies(
  filters: Partial<Filters>,
  page = 1
): Promise<DiscoverResponse> {
  const params: Record<string, string | number | undefined> = {
    page,
    sort_by: filters.sortBy,
    // Popular gate v1: (vote_count >= 50 OR popularity >= 4)
    popular_gate: filters.popularOnly !== false ? 'v1' : undefined,
    // Released only: exclude unreleased/future-dated items
    released_only: filters.releasedOnly !== false ? 'true' : undefined,
  };

  // Genre filter
  if (filters.genres && filters.genres.length > 0) {
    params.with_genres = filters.genres.join(',');
  }

  // Year range
  if (filters.yearRange) {
    params['primary_release_date.gte'] = `${filters.yearRange[0]}-01-01`;
    params['primary_release_date.lte'] = `${filters.yearRange[1]}-12-31`;
  }

  // Runtime range
  if (filters.runtimeRange) {
    if (filters.runtimeRange[0] > 0) {
      params['with_runtime.gte'] = filters.runtimeRange[0];
    }
    if (filters.runtimeRange[1] < 300) {
      params['with_runtime.lte'] = filters.runtimeRange[1];
    }
  }

  // TMDB rating range
  if (filters.tmdbRatingRange) {
    if (filters.tmdbRatingRange[0] > 0) {
      params['vote_average.gte'] = filters.tmdbRatingRange[0];
    }
    if (filters.tmdbRatingRange[1] < 10) {
      params['vote_average.lte'] = filters.tmdbRatingRange[1];
    }
  }

  // Language filter
  if (filters.languages && filters.languages.length > 0) {
    params.with_original_language = filters.languages.join('|');
  }

  // Person search (actor or director via cast/crew) - supports multiple selections
  if (filters.actorSearch && filters.actorSearch.length > 0) {
    const actorIds: number[] = [];
    for (const actorName of filters.actorSearch) {
      const people = await searchPeople(actorName);
      if (people.length > 0) {
        actorIds.push(people[0].id);
      }
    }
    if (actorIds.length > 0) {
      params.with_cast = actorIds.join(',');
    }
  }

  if (filters.directorSearch && filters.directorSearch.length > 0) {
    const directorIds: number[] = [];
    for (const directorName of filters.directorSearch) {
      const people = await searchPeople(directorName);
      if (people.length > 0) {
        directorIds.push(people[0].id);
      }
    }
    if (directorIds.length > 0) {
      params.with_crew = directorIds.join(',');
    }
  }

  return fetchFromAPI<DiscoverResponse>({
    endpoint: '/discover/movie',
    params,
  });
}

/**
 * Discover movies by emotions (used by /feel page)
 * Uses ai_emotions_evoked filter in Meilisearch
 * Evidence gated by popular gate v1: (vote_count >= 50 OR popularity >= 4)
 *
 * @param emotions - Array of emotion IDs (e.g., ['cozy', 'hopeful'])
 * @param page - Page number (default 1)
 * @param limit - Results per page (default 20)
 * @param sortBy - Sort order (default 'filmpl_rating.desc')
 */
export async function discoverByEmotions(
  emotions: string[],
  page = 1,
  limit = 20,
  sortBy = 'filmpl_rating.desc'
): Promise<MediaDiscoverResponse> {
  if (!emotions || emotions.length === 0) {
    return { page: 1, results: [], total_pages: 0, total_results: 0 };
  }

  const params: Record<string, string | number> = {
    page,
    limit,
    sort_by: sortBy,
    ai_emotions_evoked: emotions.join(','),
    popular_gate: 'v1',
  };

  const response = await fetchFromAPI<DiscoverResponse>({
    endpoint: '/discover/movie',
    params,
  });

  return {
    ...response,
    results: response.results.map(item => normalizeToMediaItem(item, 'movie')),
  };
}

export async function searchMedia(
  query: string,
  page = 1,
  mediaType: MediaType = 'movie'
): Promise<MediaDiscoverResponse> {
  const endpoint = mediaType === 'movie' ? '/search/movie' : '/search/tv';
  const response = await fetchFromAPI<DiscoverResponse>({
    endpoint,
    params: { query, page },
  });

  return {
    ...response,
    results: response.results.map(item => normalizeToMediaItem(item, mediaType)),
  };
}

export async function searchMovies(
  query: string,
  page = 1
): Promise<DiscoverResponse> {
  return fetchFromAPI<DiscoverResponse>({
    endpoint: '/search/movie',
    params: { query, page },
  });
}

interface Person {
  id: number;
  name: string;
  profile_path: string | null;
  known_for_department: string;
  popularity?: number;
}

interface PersonSearchResponse {
  results: Person[];
}

export async function searchPeople(query: string): Promise<Person[]> {
  if (!query.trim()) return [];

  const response = await fetchFromAPI<PersonSearchResponse>({
    endpoint: '/search/person',
    params: { query },
  });

  return response.results;
}

export async function getMovieDetails(movieId: number): Promise<Movie> {
  return fetchFromAPI<Movie>({
    endpoint: `/movie/${movieId}`,
    params: {
      append_to_response: 'credits,videos',
    },
  });
}

export async function getTVDetails(tvId: number): Promise<TVShow> {
  return fetchFromAPI<TVShow>({
    endpoint: `/tv/${tvId}`,
    params: {
      append_to_response: 'credits,videos',
    },
  });
}

export async function getMediaDetails(id: number, mediaType: MediaType): Promise<MediaItem> {
  if (mediaType === 'tv') {
    const tv = await getTVDetails(id);
    return normalizeToMediaItem(tv, 'tv');
  }
  const movie = await getMovieDetails(id);
  return normalizeToMediaItem(movie, 'movie');
}

export async function getMovieVideos(movieId: number): Promise<{ results: { id: string; key: string; name: string; site: string; type: string }[] }> {
  return fetchFromAPI({
    endpoint: `/movie/${movieId}/videos`,
  });
}

export interface PersonDetails {
  id: number;
  name: string;
  biography: string;
  birthday: string | null;
  deathday: string | null;
  place_of_birth: string | null;
  profile_path: string | null;
  known_for_department: string;
  also_known_as: string[];
  gender: number;
  popularity: number;
  combined_credits?: {
    cast: {
      id: number;
      title?: string;
      name?: string;
      character: string;
      release_date?: string;
      first_air_date?: string;
      poster_path: string | null;
      vote_average: number;
      media_type: 'movie' | 'tv';
    }[];
    crew: {
      id: number;
      title?: string;
      name?: string;
      job: string;
      release_date?: string;
      first_air_date?: string;
      poster_path: string | null;
      vote_average: number;
      media_type: 'movie' | 'tv';
    }[];
  };
}

export async function getPersonDetails(personId: number): Promise<PersonDetails> {
  return fetchFromAPI<PersonDetails>({
    endpoint: `/person/${personId}`,
    params: {
      append_to_response: 'combined_credits',
    },
  });
}

export async function getPopularMovies(page = 1): Promise<DiscoverResponse> {
  return fetchFromAPI<DiscoverResponse>({
    endpoint: '/movie/popular',
    params: { page },
  });
}

export async function getTrendingMovies(
  timeWindow: 'day' | 'week' = 'week'
): Promise<DiscoverResponse> {
  return fetchFromAPI<DiscoverResponse>({
    endpoint: `/trending/movie/${timeWindow}`,
  });
}

// OMDB enrichment types
export interface OMDBEnrichment {
  imdb_id: string;
  imdb_rating: number | null;
  imdb_votes: number | null;
  metacritic: number | null;
  rt_rating: number | null;
  runtime: number | null;
  rated: string | null;
  awards: string | null;
  box_office: string | null;
  plot: string | null;
  director: string | null;
  writer: string | null;
  actors: string | null;
  genre: string | null;
  language: string | null;
  country: string | null;
  year: string | null;
  type: string | null;
  ratings: Array<{ Source: string; Value: string }>;
}

/**
 * Fetch OMDB enrichment data for a movie/TV show by IMDB ID
 * Returns ratings from IMDB, Rotten Tomatoes, and Metacritic
 * Uses centralized apiFetch for consistency
 */
export async function getOMDBEnrichment(imdbId: string): Promise<OMDBEnrichment | null> {
  if (!imdbId || !imdbId.startsWith('tt')) {
    return null;
  }

  try {
    return await apiFetch<OMDBEnrichment>(`/omdb/${imdbId}`);
  } catch (error) {
    // OMDB enrichment is optional - log and return null on failure
    console.warn(`OMDB enrichment error for ${imdbId}:`, error);
    return null;
  }
}

/**
 * Get movie details with OMDB enrichment
 * Fetches TMDB details and enriches with OMDB ratings
 */
export async function getMovieDetailsEnriched(movieId: number): Promise<Movie & { omdb?: OMDBEnrichment }> {
  // Fetch movie details with external_ids appended
  const movie = await fetchFromAPI<Movie & { external_ids?: { imdb_id?: string } }>({
    endpoint: `/movie/${movieId}`,
    params: {
      append_to_response: 'credits,videos,external_ids',
    },
  });

  // If we have an IMDB ID, fetch OMDB enrichment
  const imdbId = movie.external_ids?.imdb_id || (movie as unknown as { imdb_id?: string }).imdb_id;
  if (imdbId) {
    const omdb = await getOMDBEnrichment(imdbId);
    if (omdb) {
      return {
        ...movie,
        imdb_rating: omdb.imdb_rating ?? movie.imdb_rating,
        rt_rating: omdb.rt_rating ?? movie.rt_rating,
        runtime: movie.runtime || omdb.runtime || undefined,
        omdb,
      };
    }
  }

  return movie;
}

/**
 * Get TV details with OMDB enrichment
 * Fetches TMDB details and enriches with OMDB ratings
 */
export async function getTVDetailsEnriched(tvId: number): Promise<TVShow & { omdb?: OMDBEnrichment }> {
  // Fetch TV details with external_ids appended
  const tv = await fetchFromAPI<TVShow & { external_ids?: { imdb_id?: string } }>({
    endpoint: `/tv/${tvId}`,
    params: {
      append_to_response: 'credits,videos,external_ids',
    },
  });

  // If we have an IMDB ID, fetch OMDB enrichment
  const imdbId = tv.external_ids?.imdb_id || (tv as unknown as { imdb_id?: string }).imdb_id;
  if (imdbId) {
    const omdb = await getOMDBEnrichment(imdbId);
    if (omdb) {
      return {
        ...tv,
        imdb_rating: omdb.imdb_rating ?? tv.imdb_rating,
        rt_rating: omdb.rt_rating ?? tv.rt_rating,
        runtime: tv.runtime ?? tv.episode_run_time?.[0] ?? omdb.runtime ?? undefined,
        omdb,
      };
    }
  }

  return tv;
}
